import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { IconButton } from '../buttons';
import { Tooltip } from '../tooltip';
import { useTranslation } from 'react-i18next';
import { useDeviceSize } from '@vmos2/providers';
import { Input } from '../input/Input';

type Props = {
  className?: ?string,
  onIncrease: Function,
  onDecrease: Function,
  hasNextStep: boolean,
  hasPreviousStep: boolean,
  step: number,
  counter: number,
  hasCounter: boolean,
  reachedMax?: boolean,
  filled?: boolean,
  hasCounterAsButton?: Boolean,
  size?: 'xsmall' | 'small' | 'large',
  isEditable?: boolean,
  isEditing?: boolean,
  onChangeAmount?: Function,
  onInputFocus?: Function,
  onInputBlur?: Functioon,
  enteredAmountExceeded?: boolean,
  buttonWithoutSpace?: boolean,
  title?: string | React.ReactNode | JSX.Element,
  isCentered?: Boolean,
};

type CounterProps = {
  quantity: number,
  filled?: Boolean,
  className: ?string,
  disabled?: Boolean,
  onClick: Function,
  isEditable?: boolean,
  isEditing?: boolean,
  onChangeAmount?: Function,
  onInputFocus?: Function,
  onInputBlur?: Functioon,
  enteredAmountExceeded?: boolean,
  size?: 'xsmall',
};

type IconProps = {
  onDecrease: Function,
  icon: string,
  disabled?: Boolean,
  size?: 'xsmall' | 'small' | 'large',
  buttonWithoutSpace?: boolean,
};

type ContainerProps = {
  className: ?string,
  hasCounterAsButton?: Boolean,
  decreaseButton: React.Node,
  counter?: React.Node,
  counterAsButton?: React.Node,
  isCentered?: Boolean,
};

const DecreaseQuantityIcon = ({ onDecrease, icon, disabled, size, buttonWithoutSpace }: IconProps) => {
  const theme = useTheme();
  const { t } = useTranslation('');
  return (
    <IconButton
      onClick={onDecrease}
      dataTest="decrease-quantity-btn"
      icon={icon}
      backgroundColor={theme.color.white}
      borderColor={theme.color.lightGrey}
      disabled={disabled}
      size={size || 'small'}
      tooltip={t('common:remove')}
      touchTooltip={false}
      mode="light"
      withoutSpace={buttonWithoutSpace}
    />
  );
};

const QuantityContainer = ({
  className,
  hasCounterAsButton,
  decreaseButton,
  counter,
  counterAsButton,
  isCentered,
}: ContainerProps) => {
  return (
    <Container className={className} hasCounterAsButton={hasCounterAsButton} isCentered={isCentered}>
      {decreaseButton}
      {hasCounterAsButton ? counterAsButton : counter}
    </Container>
  );
};

export const CounterComponent = ({
  quantity,
  filled,
  className,
  disabled,
  onClick,
  isEditable,
  isEditing,
  onChangeAmount,
  onInputFocus,
  onInputBlur,
  enteredAmountExceeded,
  size,
}: CounterProps) => {
  const handleClick = e => {
    e.stopPropagation();
    !disabled && onClick && onClick(e);
  };
  const { t } = useTranslation('');

  const amountInput = () => {
    return (
      <InputWrapper
        value={quantity}
        handleChange={onChangeAmount}
        type="number"
        size="small"
        onFocus={e => {
          e.target.select();
          onInputFocus();
        }}
        onBlur={onInputBlur}
        autoFocus={isEditing}
        preventNonNumericEntry
        min="1"
        max="99"
        isQuantityField
        filled={filled}
      />
    );
  };

  let quantityToRender = quantity;

  if (isEditable) {
    quantityToRender =
      isEditing && enteredAmountExceeded ? (
        <Tooltip onCreate={tip => tip.show()} content={<span>{t('alerts:quantityimitExceeded')}</span>}>
          {amountInput()}
        </Tooltip>
      ) : (
        amountInput()
      );
  }

  return (
    <CounterWrapper className={className} isEditable={isEditable} filled={filled} onClick={handleClick} size={size}>
      {quantityToRender}
    </CounterWrapper>
  );
};

/**
 * ### Theme Example
 * ```
 * "default" {
 *   ...
 *   "components": {
 *     ...
 *     "QuantityCounter": {
 *       "default": {
 *         "unitLabel": false | true
 *       }
 *     }
 *   }
 * }
 * ```
 */

export const QuantityCounter = ({
  className,
  onDecrease,
  onIncrease,
  step,
  hasNextStep,
  hasPreviousStep = true,
  counter,
  hasCounter,
  reachedMax,
  filled,
  hasCounterAsButton,
  size,
  isEditable,
  isEditing,
  onChangeAmount,
  onInputFocus,
  onInputBlur,
  enteredAmountExceeded,
  buttonWithoutSpace,
  title,
  isCentered,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation('');
  const { isMobile } = useDeviceSize();

  const plusIcon = () => (
    <IconButton
      onClick={onIncrease}
      disabled={!hasNextStep || !!reachedMax}
      dataTest="increase-quantity-btn"
      icon="Plus"
      backgroundColor={theme.color.white}
      borderColor={theme.color.lightGrey}
      tooltip={t('buttons:addAnother')}
      touchTooltip={false}
      size={size || (isMobile ? 'xsmall' : 'small')}
      mode="light"
      withoutSpace={buttonWithoutSpace}
    />
  );
  return (
    <QuantityContainer
      className={className}
      hasCounterAsButton={hasCounterAsButton}
      size={size}
      title={title}
      isCentered={isCentered}
      decreaseButton={
        <DecreaseQuantityIcon
          onDecrease={onDecrease}
          icon={step > 0 ? 'Minus' : 'Trash'}
          disabled={!hasPreviousStep || (hasCounter && step === 1 && counter === 1)}
          size={size || (isMobile ? 'xsmall' : 'small')}
          buttonWithoutSpace={buttonWithoutSpace}
        />
      }
      counter={
        <React.Fragment>
          {((hasCounter && !!counter) || isEditing) && (
            <CounterComponent
              size={size}
              isEditable={isEditable}
              isEditing={isEditing}
              onChangeAmount={onChangeAmount}
              filled={filled}
              quantity={counter}
              onInputFocus={onInputFocus}
              onInputBlur={onInputBlur}
              reachedMax={reachedMax}
              enteredAmountExceeded={enteredAmountExceeded}
            />
          )}

          {title}
          {reachedMax ? (
            <Tooltip content={<span>{t('alerts:maxSelected')}</span>}>
              <span>{plusIcon()}</span>
            </Tooltip>
          ) : (
            plusIcon()
          )}
        </React.Fragment>
      }
      counterAsButton={
        <Tooltip content={<span>{t(reachedMax ? 'alerts:maxSelected' : 'alerts:addMore')}</span>} touch={reachedMax}>
          <span>
            <CounterComponent
              filled
              quantity={counter}
              onClick={onIncrease}
              disabled={!hasNextStep || reachedMax}
              dataTest="increase-quantity-btn"
              size={size}
            />
          </span>
        </Tooltip>
      }
    />
  );
};

QuantityCounter.defaultProps = {
  className: null,
  counter: 1,
  hasCounter: true,
  isEditable: false,
};

CounterComponent.defaultProps = {
  className: null,
  quantity: 0,
  filled: false,
};

QuantityContainer.defaultProps = {
  className: null,
};

const Container = styled('div')`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 8px;

  ${({ isCentered }) => isCentered && `justify-content: center`};
`;

const CounterWrapper = styled('span')`
  position: relative;
  font-size: ${({ theme }) => theme.fontSize.sm};
  min-width: 12px;
  text-align: center;

  ${({ isEditable, size }) =>
    !isEditable &&
    `&::after {
      content: '';
      position: absolute;
      width: ${size === 'xsmall' ? '32px' : '40px'};
      height: ${size === 'xsmall' ? '32px' : '40px'};
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }`}

  ${({ theme, filled }) =>
    filled &&
    `
    width: 32px;
    height: 32px;
    border-radius: ${theme.borderRadius.circle};
    background: ${theme.color.brand};
    color: ${theme.color.white};
    display: flex;
    justify-content: center;
    align-items: center;
  `};

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    font-size: ${({ theme }) => theme.fontSize.md};
  }

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    min-width: 24px;
    font-size: ${({ theme }) => theme.fontSize.xl};

    ${({ filled }) =>
      filled &&
      `
      height: 40px;
      width: 40px;
    `}
  }

  @media (${({ theme }) => theme.breakpoint.kiosk}) {
    min-width: 48px;

    ${({ filled }) =>
      filled &&
      `
      height: 48px;
    `}
  }
`;

const InputWrapper = styled(Input)`
  @media (${({ theme }) => theme.breakpoint.mobile}) {
    width: 30px;
    height: 30px;
    padding: 2px;
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`;
